import { useState } from 'react';
import type { ReactNode } from 'react';
import { Typography } from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  WarningOutlined,
  DeleteOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Invitation, InvitationStatus } from '../../types/business';

const { Text } = Typography;

function formatPhone(phone: string): string {
  if (phone.length === 12 && phone.startsWith('998')) {
    return `+${phone.slice(0, 3)} ${phone.slice(3, 5)} ${phone.slice(5, 8)} ${phone.slice(8)}`;
  }
  return phone.startsWith('+') ? phone : `+${phone}`;
}

const STATUS_CONFIG: Record<InvitationStatus, { color: string; icon: ReactNode }> = {
  PENDING:  { color: '#fa8c16', icon: <ClockCircleOutlined /> },
  ACCEPTED: { color: '#1f8b4c', icon: <CheckCircleOutlined /> },
  REVOKED:  { color: '#9ca3af', icon: <StopOutlined /> },
  EXPIRED:  { color: '#ef4444', icon: <WarningOutlined /> },
};

interface Props {
  invitation: Invitation;
  onRevoke?: (invitation: Invitation) => void;
}

export default function InvitationRow({ invitation, onRevoke }: Props) {
  const { t } = useTranslation('owner');
  const [revokePressed, setRevokePressed] = useState(false);

  const config = STATUS_CONFIG[invitation.status];
  const statusLabel = t(`invitations.statuses.${invitation.status}`, {
    defaultValue: invitation.status,
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        background: 'var(--bl-surface, #fff)',
        border: '1px solid var(--bl-divider, #ebebeb)',
        borderRadius: 14,
        boxShadow: 'var(--bl-shadow-soft, 0 2px 10px rgba(0,0,0,0.04))',
      }}
    >
      {/* Status disc */}
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: 13,
          background: `linear-gradient(135deg, ${config.color}14 0%, ${config.color}30 100%)`,
          border: `1px solid ${config.color}26`,
          color: config.color,
          fontSize: 17,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {config.icon}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
          <PhoneOutlined style={{ fontSize: 10, color: 'var(--bl-text-muted, #9ca3af)' }} />
          <Text
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--bl-text-strong, #0f172a)',
              letterSpacing: -0.1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatPhone(invitation.phoneNumber)}
          </Text>
        </div>

        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 10,
            fontWeight: 700,
            color: config.color,
            background: `${config.color}14`,
            border: `1px solid ${config.color}26`,
            borderRadius: 999,
            padding: '2px 8px',
            letterSpacing: 0.3,
            textTransform: 'uppercase',
          }}
        >
          <span
            aria-hidden
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: config.color,
            }}
          />
          {statusLabel}
        </span>
      </div>

      {/* Revoke button (only for PENDING) */}
      {invitation.status === 'PENDING' && onRevoke && (
        <button
          type="button"
          onClick={() => onRevoke(invitation)}
          onTouchStart={() => setRevokePressed(true)}
          onTouchEnd={() => setRevokePressed(false)}
          onTouchCancel={() => setRevokePressed(false)}
          onMouseDown={() => setRevokePressed(true)}
          onMouseUp={() => setRevokePressed(false)}
          onMouseLeave={() => setRevokePressed(false)}
          aria-label={t('invitations.revokeConfirm')}
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.20)',
            color: '#ef4444',
            fontSize: 13,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transform: revokePressed ? 'scale(0.92)' : 'scale(1)',
            transition: 'transform 0.1s ease',
            WebkitTapHighlightColor: 'transparent',
            outline: 'none',
            flexShrink: 0,
          }}
        >
          <DeleteOutlined />
        </button>
      )}
    </div>
  );
}
